import React, { useState, useEffect, useRef } from 'react';
import { Search, X, User, Briefcase, FileText, Star, Calendar, Clock } from 'lucide-react';

interface SearchResult {
  users?: User[];
  trainers?: Trainer[];
  posts?: Post[];
}

interface User {
  id: string;
  username: string;
  first_name: string;
  last_name: string;
  bio?: string;
  profile_picture: string | null;
  user_type: string;
  date_joined?: string;
}

interface Trainer {
  id: string;
  username: string;
  first_name: string;
  last_name: string;
  bio?: string;
  profile_picture: string | null;
  specializations: string;
  certifications?: string;
  years_experience?: number;
  hourly_rate: number;
  rate_30_min?: number;
  rating: number;
  total_sessions?: number;
  created_at?: string;
}

interface Post {
  id: string;
  content: string;
  image?: string;
  likes_count: number;
  comments_count?: number;
  created_at: string;
  author: {
    id: string;
    username: string;
    first_name: string;
    last_name: string;
    profile_picture: string | null;
    user_type: string;
  };
}

interface Suggestion {
  type: 'user' | 'trainer';
  id: string;
  text: string;
  subtext?: string;
  image?: string | null;
  rating?: number;
}

interface SearchComponentProps {
  onNavigateToProfile?: (userId: string) => void;
  onNavigateToPost?: (postId: string) => void;
  onNavigateToTrainer?: (trainerId: string) => void;
}

const SearchComponent: React.FC<SearchComponentProps> = ({
  onNavigateToProfile,
  onNavigateToPost,
  onNavigateToTrainer
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult>({});
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showFullResults, setShowFullResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeType, setActiveType] = useState('all');
  const [totalCount, setTotalCount] = useState(0);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<number | undefined>(undefined);

  // API base URL - adjust this to match your Django server
  const API_BASE_URL = 'https://aztrainersbackend.onrender.com';

  useEffect(() => {
    // Debounced search for suggestions
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (query.length >= 2) {
      debounceRef.current = setTimeout(() => {
        fetchSuggestions(query);
      }, 300);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query]);

  const fetchSuggestions = async (searchQuery: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/search/suggestions/?q=${encodeURIComponent(searchQuery)}`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSuggestions(data.suggestions || []);
        setShowSuggestions(true);
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    }
  };

  const fetchFullResults = async (searchQuery: string, searchType: string = 'all') => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_BASE_URL}/api/search/?q=${encodeURIComponent(searchQuery)}&type=${searchType}&limit=50`,
        {
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setResults(data.results || data); // Handle both new and old response formats

        // Calculate total count
        const userCount = data.results?.users?.length || data.users?.length || 0;
        const trainerCount = data.results?.trainers?.length || data.trainers?.length || 0;
        const postCount = data.results?.posts?.length || data.posts?.length || 0;
        setTotalCount(data.total_count || (userCount + trainerCount + postCount));

        setShowFullResults(true);
        setShowSuggestions(false);
      } else {
        const errorData = await response.json();
        console.error('Search error:', errorData.error);
      }
    } catch (error) {
      console.error('Error fetching search results:', error);
    }
    setLoading(false);
  };

  const handleSearch = (searchQuery: string = query) => {
    if (searchQuery.trim()) {
      fetchFullResults(searchQuery, activeType);
    }
  };

  const handleTypeChange = (type: string) => {
    setActiveType(type);
    if (query.trim()) {
      fetchFullResults(query, type);
    }
  };

  const handleSuggestionClick = (suggestion: Suggestion) => {
    setQuery(suggestion.text);
    setShowSuggestions(false);
    fetchFullResults(suggestion.text, suggestion.type === 'trainer' ? 'trainers' : 'users');
  };

  const handleUserClick = (userId: string) => {
    setShowFullResults(false);
    if (onNavigateToProfile) {
      onNavigateToProfile(userId);
    }
  };

  const handleTrainerClick = (trainerId: string) => {
    setShowFullResults(false);
    if (onNavigateToTrainer) {
      onNavigateToTrainer(trainerId);
    }
  };

  const handlePostClick = (postId: string) => {
    setShowFullResults(false);
    if (onNavigateToPost) {
      onNavigateToPost(postId);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getFullName = (firstName: string, lastName: string) => {
    return `${firstName} ${lastName}`.trim() || 'Unknown User';
  };

  const truncateText = (text: string, maxLength: number) => {
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // Get arrays for display, handling both old and new result formats
  const displayUsers = results.users || [];
  const displayTrainers = results.trainers || [];
  const displayPosts = results.posts || [];

  return (
    <div className="relative w-full max-w-2xl mx-auto">
      {/* Search Input */}
      <div className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            ref={searchInputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            onFocus={() => query.length >= 2 && setShowSuggestions(true)}
            placeholder="Search users, trainers, and posts..."
            className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E6A756] focus:border-transparent text-lg"
          />
          {query && (
            <button
              onClick={() => {
                setQuery('');
                setSuggestions([]);
                setShowSuggestions(false);
                setShowFullResults(false);
              }}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Search Button */}
        <button
          onClick={() => handleSearch()}
          disabled={!query.trim() || loading}
          className="mt-2 w-full bg-[#E6A756] text-[#5D4037] py-2 px-4 rounded-lg hover:bg-[#D2824C] disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
        >
          {loading ? 'Searching...' : 'Search'}
        </button>
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
          {suggestions.map((suggestion, index) => (
            <div
              key={`${suggestion.type}-${suggestion.id}-${index}`}
              onClick={() => handleSuggestionClick(suggestion)}
              className="flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
            >
              <div className="flex-shrink-0 mr-3">
                {suggestion.image ? (
                  <img
                    src={suggestion.image}
                    alt={suggestion.text}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                    {suggestion.type === 'trainer' ? (
                      <Briefcase className="h-5 w-5 text-gray-500" />
                    ) : (
                      <User className="h-5 w-5 text-gray-500" />
                    )}
                  </div>
                )}
              </div>
              <div className="flex-1">
                <div className="font-medium text-gray-900">{suggestion.text}</div>
                {suggestion.subtext && (
                  <div className="text-sm text-gray-500">{suggestion.subtext}</div>
                )}
                {suggestion.rating !== undefined && (
                  <div className="flex items-center mt-1">
                    <Star className="h-3 w-3 text-yellow-400 fill-current" />
                    <span className="text-xs text-gray-500 ml-1">{suggestion.rating.toFixed(1)}</span>
                  </div>
                )}
              </div>
              <div className="flex-shrink-0">
                <span className={`px-2 py-1 text-xs rounded-full 
                ${suggestion.type === 'trainer'
                    ? 'bg-[#B8C5A6] text-[#5D4037]'
                    : 'bg-[#F8F4EE] text-[#5D4037] border border-[#D4C4B0]'
                  }`}>
                  {suggestion.type === 'trainer' ? 'Trainer' : 'User'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Full Search Results Modal */}
      {showFullResults && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-lg w-full max-w-4xl mt-8 mb-8">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <div>
                <h2 className="text-xl font-semibold">Search Results</h2>
                <p className="text-gray-600">
                  {totalCount} results for "{query}"
                </p>
              </div>
              <button
                onClick={() => setShowFullResults(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Category Filters */}
            <div className="px-6 py-4 border-b">
              <div className="flex space-x-4">
                {[
                  { key: 'all', label: 'All', count: totalCount },
                  { key: 'users', label: 'Users', count: displayUsers.length },
                  { key: 'trainers', label: 'Trainers', count: displayTrainers.length },
                  { key: 'posts', label: 'Posts', count: displayPosts.length },
                ].map((category) => (
                  <button
                    key={category.key}
                    onClick={() => handleTypeChange(category.key)}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeType === category.key
                      ? 'bg-[#E6A756] text-[#5D4037]'
                      : 'bg-[#F8F4EE] text-[#5D4037] hover:bg-[#F5EFE7] border border-[#D4C4B0]'
                      }`}
                  >
                    {category.label} ({category.count})
                  </button>
                ))}
              </div>
            </div>

            {/* Results Content */}
            <div className="p-6 max-h-96 overflow-y-auto">
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E6A756] mx-auto"></div>
                  <p className="mt-4 text-gray-600">Searching...</p>
                </div>
              ) : (
                <>
                  {/* Users Section */}
                  {(activeType === 'all' || activeType === 'users') && displayUsers.length > 0 && (
                    <div className="mb-8">
                      <h3 className="text-lg font-semibold mb-4 flex items-center">
                        <User className="h-5 w-5 mr-2" />
                        Users ({displayUsers.length})
                      </h3>
                      <div className="grid gap-4">
                        {displayUsers.map((user) => (
                          <div
                            key={user.id}
                            onClick={() => handleUserClick(user.id)}
                            className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                          >
                            <div className="flex-shrink-0 mr-4">
                              {user.profile_picture ? (
                                <img
                                  src={user.profile_picture}
                                  alt={getFullName(user.first_name, user.last_name)}
                                  className="w-12 h-12 rounded-full object-cover"
                                />
                              ) : (
                                <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center">
                                  <User className="h-6 w-6 text-gray-500" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900">
                                {getFullName(user.first_name, user.last_name)}
                              </h4>
                              <p className="text-gray-600">@{user.username}</p>
                              {user.bio && (
                                <p className="text-sm text-gray-500 mt-1">
                                  {truncateText(user.bio, 100)}
                                </p>
                              )}
                              {user.date_joined && (
                                <div className="flex items-center mt-2 text-xs text-gray-500">
                                  <Calendar className="h-3 w-3 mr-1" />
                                  Joined {formatDate(user.date_joined)}
                                </div>
                              )}
                            </div>
                            <div className="flex-shrink-0">
                              <span className="px-3 py-1 bg-[#F8F4EE] text-[#5D4037] text-sm rounded-full border border-[#D4C4B0]">
                                {user.user_type}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Trainers Section */}
                  {(activeType === 'all' || activeType === 'trainers') && displayTrainers.length > 0 && (
                    <div className="mb-8">
                      <h3 className="text-lg font-semibold mb-4 flex items-center">
                        <Briefcase className="h-5 w-5 mr-2" />
                        Trainers ({displayTrainers.length})
                      </h3>
                      <div className="grid gap-4">
                        {displayTrainers.map((trainer) => (
                          <div
                            key={trainer.id}
                            onClick={() => handleTrainerClick(trainer.id)}
                            className="flex items-start p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                          >
                            <div className="flex-shrink-0 mr-4">
                              {trainer.profile_picture ? (
                                <img
                                  src={trainer.profile_picture}
                                  alt={getFullName(trainer.first_name, trainer.last_name)}
                                  className="w-12 h-12 rounded-full object-cover"
                                />
                              ) : (
                                <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center">
                                  <Briefcase className="h-6 w-6 text-gray-500" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900">
                                {getFullName(trainer.first_name, trainer.last_name)}
                              </h4>
                              <p className="text-gray-600">@{trainer.username}</p>

                              <div className="flex items-center mt-1">
                                <Star className="h-4 w-4 text-yellow-400 fill-current" />
                                <span className="text-sm text-gray-600 ml-1">
                                  {trainer.rating.toFixed(1)}
                                  {trainer.total_sessions && ` • ${trainer.total_sessions} sessions`}
                                </span>
                              </div>

                              {trainer.years_experience && (
                                <p className="text-sm text-gray-600 mt-1">
                                  {trainer.years_experience} years experience
                                </p>
                              )}

                              <p className="text-sm text-gray-500 mt-1">
                                {truncateText(trainer.specializations, 80)}
                              </p>

                              {trainer.bio && (
                                <p className="text-sm text-gray-500 mt-1">
                                  {truncateText(trainer.bio, 100)}
                                </p>
                              )}
                            </div>
                            <div className="flex-shrink-0 text-right">
                              <div className="text-lg font-semibold text-[#8B5A3C]">
                                ${trainer.hourly_rate}/hr
                              </div>
                              {trainer.rate_30_min && (
                                <div className="text-sm text-gray-500">
                                  ${trainer.rate_30_min}/30min
                                </div>
                              )}
                              <span className="px-3 py-1 bg-[#B8C5A6] text-[#5D4037] text-sm rounded-full mt-2 inline-block font-medium">
                                Trainer
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Posts Section */}
                  {(activeType === 'all' || activeType === 'posts') && displayPosts.length > 0 && (
                    <div className="mb-8">
                      <h3 className="text-lg font-semibold mb-4 flex items-center">
                        <FileText className="h-5 w-5 mr-2" />
                        Posts ({displayPosts.length})
                      </h3>
                      <div className="grid gap-4">
                        {displayPosts.map((post) => (
                          <div
                            key={post.id}
                            onClick={() => handlePostClick(post.id)}
                            className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                          >
                            <div className="flex items-center mb-3">
                              <div className="flex-shrink-0 mr-3">
                                {post.author.profile_picture ? (
                                  <img
                                    src={post.author.profile_picture}
                                    alt={getFullName(post.author.first_name, post.author.last_name)}
                                    className="w-8 h-8 rounded-full object-cover"
                                  />
                                ) : (
                                  <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                                    <User className="h-4 w-4 text-gray-500" />
                                  </div>
                                )}
                              </div>
                              <div className="flex-1">
                                <h5 className="font-medium text-gray-900">
                                  {getFullName(post.author.first_name, post.author.last_name)}
                                </h5>
                                <div className="flex items-center text-xs text-gray-500">
                                  <Clock className="h-3 w-3 mr-1" />
                                  {formatDate(post.created_at)}
                                </div>
                              </div>
                              <span className={`px-2 py-1 text-xs rounded-full ${post.author.user_type === 'trainer'
                                  ? 'bg-[#B8C5A6] text-[#5D4037]'
                                  : 'bg-[#F8F4EE] text-[#5D4037] border border-[#D4C4B0]'
                                }`}>
                                {post.author.user_type}
                              </span>
                            </div>

                            <p className="text-gray-700 mb-3">
                              {post.content}
                            </p>

                            {post.image && (
                              <img
                                src={post.image}
                                alt="Post image"
                                className="w-full h-32 object-cover rounded-lg mb-3"
                              />
                            )}

                            <div className="flex items-center space-x-4 text-sm text-gray-500">
                              <span>❤️ {post.likes_count} likes</span>
                              {post.comments_count !== undefined && (
                                <span>💬 {post.comments_count} comments</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* No Results */}
                  {totalCount === 0 && !loading && (
                    <div className="text-center py-8">
                      <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
                      <p className="text-gray-600">
                        Try adjusting your search terms or browse different categories.
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchComponent;